import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuDroplet, LuCheck } from "react-icons/lu";
import { toColor } from "../../../../common/common_tools";
import "./color_picker_input.scss";

export default function ColorPicker({ value = "#000000", onChange, confirm = false }) {
  const parse = (v) => toColor(v);

  const { newColor: initColor, alpha: initAlpha } = parse(value);
  const [color, setColor] = useState(initColor);
  const [alpha, setAlpha] = useState(initAlpha);
  // In confirm mode changes are buffered locally until the user clicks ✓.
  const [dirty, setDirty] = useState(false);
  const nativeRef = useRef(null);

  useEffect(() => {
    const { newColor, alpha: a } = parse(value);
    setColor(newColor);
    setAlpha(a);
    setDirty(false);
  }, [value]);

  const hexToRgba = (hex, a) => {
    const c = (hex.startsWith("#") ? hex.substring(1) : hex).padEnd(6, "0");
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${a / 100})`;
  };

  const emit = (hex, a) => onChange({ color: hexToRgba(hex, a) });

  // Apply a change: buffer it in confirm mode, otherwise emit immediately.
  const commit = (hex, a) => {
    if (confirm) setDirty(true);
    else emit(hex, a);
  };

  const handleNativeChange = (e) => {
    setColor(e.target.value);
    // Picking a solid colour while fully transparent (default bg) would keep the
    // swatch invisible and the change with no visible effect — promote to opaque.
    const a = alpha === 0 ? 100 : alpha;
    if (a !== alpha) setAlpha(a);
    commit(e.target.value, a);
  };

  const handleHexInput = (e) => {
    let v = e.target.value;
    if (!v.startsWith("#")) v = "#" + v;
    setColor(v);
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      const a = alpha === 0 ? 100 : alpha;
      if (a !== alpha) setAlpha(a);
      commit(v, a);
    }
  };

  const handleAlphaInput = (e) => {
    const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
    setAlpha(v);
    commit(color, v);
  };

  const handleConfirm = () => {
    emit(color, alpha);
    setDirty(false);
  };

  return (
    <div className="cp-row">
      <div className="cp-color-box" onClick={() => nativeRef.current?.click()}>
        <div className="cp-swatch">
          <div className="cp-swatch-fill" style={{ backgroundColor: hexToRgba(color, alpha) }} />
        </div>
        <input
          ref={nativeRef}
          type="color"
          value={color}
          onChange={handleNativeChange}
          className="cp-native"
          tabIndex={-1}
        />
        <input
          type="text"
          value={color}
          onChange={handleHexInput}
          onClick={(e) => e.stopPropagation()}
          className="cp-hex"
          spellCheck={false}
          maxLength={7}
        />
      </div>

      <div className="cp-alpha-box" title={i18n.t("styles.opacity") || "Opacity"}>
        <input
          type="number"
          min="0"
          max="100"
          value={alpha}
          onChange={handleAlphaInput}
          className="cp-alpha-input"
          aria-label={i18n.t("styles.opacity") || "Opacity"}
        />
        <span className="cp-alpha-pct">%</span>
        <LuDroplet size={15} color="#9ca3af" style={{ flexShrink: 0 }} aria-hidden="true" />
      </div>

      {confirm && (
        <button
          type="button"
          className="cp-confirm"
          onClick={handleConfirm}
          disabled={!dirty}
          title={i18n.t("Confirm") || "Confirm"}
          aria-label={i18n.t("Confirm") || "Confirm"}
        >
          <LuCheck size={16} />
        </button>
      )}
    </div>
  );
}

ColorPicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  // When true, changes are buffered and only applied when the ✓ button is clicked.
  confirm: PropTypes.bool,
};
